import { type ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  FilePlus2,
  FileText,
  Globe2,
  LayoutDashboard,
  Settings,
  X,
  type LucideIcon,
} from "lucide-react";

import { AdminHeader, type AdminHeaderProps } from "@/components/AdminHeader";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface AdminLayoutProps extends AdminHeaderProps {
  children: ReactNode;
  /**
   * Remove the default padding from the content wrapper.
   */
  disableContentPadding?: boolean;
  /**
   * @deprecated O layout agora utiliza sempre o esquema claro.
   */
  tone?: "dark" | "light";
}

type NavItem = {
  label: string;
  to: string;
  icon: LucideIcon;
  description?: string;
  match?: string[];
  exact?: boolean;
  exclude?: string[];
};

const navItems: NavItem[] = [
  {
    label: "Central de Inteligência TocToc",
    description: "Resumo estratégico e próximos passos",
    to: "/admin",
    icon: LayoutDashboard,
    match: ["/admin"],
    exact: true,
  },
  {
    label: "Status do Ecossistema",
    description: "Variáveis críticas e integrações",
    to: "/admin/health",
    icon: Activity,
  },
  {
    label: "Setup Administrativo",
    description: "Configurações essenciais do painel",
    to: "/admin/setup",
    icon: Settings,
  },
  {
    label: "Posts",
    description: "Listar publicações",
    to: "/admin/posts",
    icon: FileText,
    match: ["/admin/posts"],
    exclude: ["/admin/posts/new"],
  },
  {
    label: "Novo Post",
    description: "Criar conteúdo inédito",
    to: "/admin/posts/new",
    icon: FilePlus2,
    exact: true,
  },
  {
    label: "Páginas",
    description: "Listar e otimizar rotas",
    to: "/admin/pages",
    icon: Globe2,
    match: ["/admin/pages"],
  },
];

export function AdminLayout({
  children,
  disableContentPadding,
  tone: _tone,
  ...headerProps
}: AdminLayoutProps) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  const isActive = (item: NavItem) => {
    const matches = item.match ?? [item.to];
    return matches.some(path => {
      if (item.exact) {
        return location.pathname === path;
      }

      if (location.pathname === path) return true;
      const matchesPath = location.pathname.startsWith(`${path}/`);
      if (!matchesPath) {
        return false;
      }

      if (item.exclude) {
        const isExcluded = item.exclude.some(exclude => {
          if (location.pathname === exclude) return true;
          return location.pathname.startsWith(`${exclude}/`);
        });
        if (isExcluded) {
          return false;
        }
      }

      return true;
    });
  };

  return (
    <div className="relative flex min-h-screen bg-slate-50 text-slate-900">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-sm transition-opacity md:hidden"
          role="presentation"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-full max-w-[280px] flex-col border-r border-slate-200 bg-white/95 backdrop-blur transition-transform duration-200 md:static md:w-[280px] md:translate-x-0 md:shadow-none",
          sidebarOpen ? "flex translate-x-0" : "hidden -translate-x-full md:flex"
        )}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-5">
            <Link to="/admin" className="flex items-center gap-3">
              <img
                src="/lovable-uploads/24e0e29e-3e10-43db-8be9-1cc4bc18ab2f.png"
                alt="TocToc"
                className="h-9 w-auto"
              />
              <div className="flex flex-col leading-tight">
                <span className="text-base font-semibold text-slate-900">TocToc</span>
                <span className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-400">Central Admin</span>
              </div>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full text-slate-500 hover:text-slate-900 md:hidden"
              onClick={() => setSidebarOpen(false)}
              aria-label="Fechar menu de navegação"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <nav className="flex-1 space-y-6 overflow-y-auto px-4 py-6">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-400">Navegação</span>
              <div className="mt-4 space-y-1">
                {navItems.map(item => {
                  const Icon = item.icon;
                  const active = isActive(item);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={cn(
                        "group flex items-start gap-3 rounded-xl px-3 py-3 transition-colors",
                        active
                          ? "bg-orange-50 text-[#FF5605] shadow-sm"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      )}
                    >
                      <div
                        className={cn(
                          "flex h-9 w-9 items-center justify-center rounded-lg border text-sm font-medium",
                          active
                            ? "border-[#FF5605]/30 bg-white text-[#FF5605]"
                            : "border-slate-200 bg-white text-slate-400 group-hover:border-[#FF5605]/30 group-hover:text-[#FF5605]"
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold leading-tight text-pretty [overflow-wrap:anywhere]">
                          {item.label}
                        </span>
                        {item.description && (
                          <span
                            className={cn(
                              "text-xs font-normal leading-snug text-pretty [overflow-wrap:anywhere]",
                              active ? "text-[#FF5605]/80" : "text-slate-500"
                            )}
                          >
                            {item.description}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/70 p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-900">Precisa de suporte?</h3>
              <p className="mt-1 text-xs text-slate-500">
                A Central Toc Toc está pronta para apoiar o time com insights e otimizações.
              </p>
              <Button asChild variant="outline" className="mt-3 w-full border-[#3332FC]/30 text-[#3332FC] hover:bg-[#3332FC]/10">
                <Link to="/admin/health">Ver status da plataforma</Link>
              </Button>
            </div>
          </nav>
        </div>
      </aside>

      <div className="flex flex-1 flex-col bg-slate-100">
        <div className="flex flex-1 flex-col bg-white shadow-none md:rounded-l-[32px] md:border-l md:border-slate-200 md:shadow-xl md:backdrop-blur md:ml-6">
          <AdminHeader
            {...headerProps}
            onToggleSidebar={toggleSidebar}
            isSidebarOpen={sidebarOpen}
          />

          <main className="flex-1 overflow-x-hidden">
            <div
              className={cn(
                "w-full pb-16",
                disableContentPadding ? "px-0" : "px-4 sm:px-6 lg:px-10",
                disableContentPadding ? "pt-12" : "pt-8",
              )}
            >
              <div className="w-full max-w-7xl space-y-8 lg:space-y-12 min-w-0">{children}</div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default AdminLayout;
